import './landing.css';
import { Nav } from './Nav';
import type { AppPage } from './LandingPage';

interface PrivacyPageProps {
  onNavigate: (page: AppPage) => void;
  onGetStarted: () => void;
  onLogin: () => void;
  isAuthenticated?: boolean;
}

export function PrivacyPage({ onNavigate, onGetStarted, onLogin, isAuthenticated }: PrivacyPageProps) {
  return (
    <div className="legal-page">
      <Nav currentPage="privacy" onNavigate={onNavigate} onGetStarted={onGetStarted} onLogin={onLogin} isAuthenticated={isAuthenticated} />

      <div className="legal-body">
        <p className="eyebrow legal-eyebrow">Legal</p>
        <h1 className="legal-h1">Privacy Policy</h1>
        <p className="legal-date">Last updated: June 2, 2026</p>

        <div className="legal-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Salifly ("we," "us," or "our") operates the Salifly Chrome extension and web website
              (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose,
              and protect your information when you use our Service.
            </p>
            <p>
              By using Salifly, you agree to the practices described in this policy. If you do not agree,
              please discontinue use of the Service.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            <p><strong>Account information.</strong> When you sign up, we collect your name and email address
              via Google OAuth or email magic link. We do not receive or store your Google password.</p>
            <p><strong>Profile data.</strong> During onboarding, we ask for your industry, role, and preferred
              communication channels. This personalizes the drafts Salifly generates for you. You can update or delete this
              at any time from your account settings.</p>
            <p><strong>Usage metadata.</strong> We collect information about how you use the Service:
              which platforms you analyze (Gmail, LinkedIn, Salesforce, etc.), analysis frequency, feature
              usage patterns, and session timestamps. This is aggregated and never includes message content.</p>
            <p><strong>Analysis data.</strong> When you run an analysis, the extension captures the current
              page and reads the conversation thread. Before this text leaves your device it passes through
              our privacy filter (see Section 3). The privacy-filtered conversation text is stored as an
              interaction snapshot — together with derived metadata (deal state, confidence level, and draft
              history) — linked to your account, so you can review your activity in your dashboard. The
              filtered conversation content is automatically deleted within 30 days; the derived metadata is
              retained as described in Section 6 (Data Retention). Your conversation content and drafts are
              never used to train any AI models — ours or our providers' (see Section 5). To enable real-time
              inline suggestions, the draft text you type in compose fields is transmitted directly to our AI services.
              This draft text is not pre-filtered by our privacy filter to ensure the AI can map and replace exact
              phrases in your composer. When suggestions are served, a privacy-filtered excerpt of your draft,
              the suggestions shown, and which suggestion you accepted are stored to your account so we can
              measure and improve suggestion quality; these records are automatically deleted within 30 days.</p>
            <p><strong>Sent replies (auto-learning).</strong> On paid plans, Salifly includes an auto-learning
              feature that personalizes future drafts to your own writing style. When you send a reply in a
              messaging tool where Salifly is active, the extension captures the text of the message you sent
              and the surrounding conversation context, and uses it to build a private knowledge base linked to
              your account — your characteristic tone and phrasing, plus durable business facts (such as your
              pricing, hours, or plan tiers) that recur across your conversations. This knowledge base is used
              only to generate better drafts for <em>you</em>; it is private to your account (enforced by
              row-level security) and is never shared with other users or used to train shared or third-party
              models (see Section 5). It is retained until you delete it or close your account (see Section 6).
              Free-plan users are not subject to this capture.</p>
          </section>

          <section>
            <h2>3. Privacy Protections Built Into the Extension</h2>
            <p>Salifly is built with privacy as a first-class requirement, not an afterthought:</p>
            <ul>
              <li>
                <strong>Screenshots of the page you analyze.</strong> When you run an analysis, the extension
                captures a screenshot of the active tab, downscales it (to roughly 1024px wide) to reduce size,
                and sends it to our AI processing providers (Google Vertex AI for visual analysis, and Anthropic
                as part of generating drafts). A screenshot may contain personal information that is visible on
                screen at the time of capture (for example names, email addresses, or phone numbers shown in your
                CRM). Our text privacy filter (below) operates on scraped text only and does not remove
                information rendered inside the screenshot image. Screenshots are used solely to process your
                request and are not retained after the session or used to train any models.
              </li>
              <li>
                <strong>Personal information removal from text.</strong> When performing a full page analysis,
                our privacy filter automatically strips email addresses, phone numbers, Social Security Numbers,
                card-like numbers, account/policy identifiers, monetary amounts, and US street addresses from the
                scraped conversation thread before it leaves your device. Note that the privacy filter is not applied
                to the draft text typed for inline suggestions or to the sent replies stored for auto-learning (paid plans)
                so that the Service can analyze your exact phrasing. All such data is processed under strict enterprise
                commercial terms that prohibit retention or model training by our AI providers.
              </li>
              <li>
                <strong>No keystroke logging.</strong> We do not monitor your typing, access your clipboard,
                or read data from pages you haven't explicitly analyzed.
              </li>
              <li>
                <strong>Local conversation history.</strong> Your conversation history within the extension
                is stored locally on your device and is not synced to our servers.
              </li>
              <li>
                <strong>A lightweight extension, powered by the cloud.</strong> Salifly keeps its analysis
                capabilities in the cloud so the extension stays fast and your browser stays clean. Install
                in seconds, get full coverage from the first click.
              </li>
            </ul>
          </section>

          <section>
            <h2>4. Third-Party Services</h2>
            <p>We share data with the following services to operate Salifly:</p>
            <ul>
              <li>
                <strong>Supabase.</strong> Authentication, user profiles, interaction snapshots, and your
                auto-learning knowledge base (see Section 2) are stored in our Supabase (PostgreSQL) database,
                hosted on AWS. Data is encrypted at rest and in transit. Row Level Security (RLS) policies
                ensure each user can only access their own records.
              </li>
              <li>
                <strong>Google (OAuth).</strong> If you sign in with Google, your name and email are shared with
                us by Google per their standard OAuth 2.0 flow. We do not receive your Google password or access
                to your Google account data beyond what you authorize.
              </li>
              <li>
                <strong>Anthropic.</strong> Privacy-protected conversation text, the visual context derived from
                your screenshot, and your profile preferences are sent to Anthropic's API to generate draft
                messages. Anthropic's{' '}
                <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer">
                  privacy policy
                </a>{' '}
                governs their handling of API data.
              </li>
              <li>
                <strong>Google (Vertex AI processing services).</strong> Your downscaled screenshot and the same
                privacy-protected conversation text are processed by Google Vertex AI for visual analysis and
                lead context extraction. Google's{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                  privacy policy
                </a>{' '}
                and{' '}
                <a href="https://ai.google.dev/gemini-api/terms" target="_blank" rel="noopener noreferrer">
                  API terms
                </a>{' '}
                apply.
              </li>
            </ul>
            <p>
              We do not sell your personal data to third parties, use it for advertising, or share it with
              parties other than those listed above.
            </p>
          </section>

          <section>
            <h2>5. AI Processing and Model Training</h2>
            <p>
              <strong>We do not use your data to train AI models.</strong> Salifly never uses your
              conversation content, screenshots, generated drafts, or any other customer data to train,
              fine-tune, or improve any artificial intelligence or machine-learning model — neither our own
              models nor those of our providers.
            </p>
            <p>
              To generate drafts, Salifly sends your privacy-filtered conversation text and downscaled
              screenshot to our AI processing providers: the Anthropic API and Google Vertex AI (on Google
              Cloud). We use these providers' commercial API services, under commercial terms that prohibit the
              use of submitted data to train their models. These providers process your request and return a
              result; they do not retain your inputs to improve their models. Anthropic's{' '}
              <a href="https://www.anthropic.com/legal/commercial-terms" target="_blank" rel="noopener noreferrer">
                commercial terms
              </a>{' '}
              and Google Vertex AI's{' '}
              <a href="https://cloud.google.com/terms/service-terms" target="_blank" rel="noopener noreferrer">
                service terms
              </a>{' '}
              govern their handling of API data and confirm that customer data is not used for model training.
            </p>
            <p>
              In short: your data is used only to deliver the feature you requested — generating a draft — and
              nothing else. We do not sell it, we do not use it for advertising, and we do not feed it into any
              model training pipeline.
            </p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>We retain your data for the following periods:</p>
            <ul>
              <li><strong>Account and profile data.</strong> Retained until you delete your account.</li>
              <li>
                <strong>Privacy-filtered conversation content.</strong> The conversation text stored with an
                interaction snapshot is automatically deleted within 30 days.
              </li>
              <li>
                <strong>Interaction metadata and draft history.</strong> Derived metadata (deal state,
                confidence level, draft history) is retained for 90 days to power your website analytics, then
                automatically deleted.
              </li>
              <li>
                <strong>Usage metadata.</strong> Retained for 12 months in aggregated, anonymized form.
              </li>
              <li>
                <strong>Auto-learning knowledge base.</strong> The tone examples and business facts derived
                from your sent replies (paid plans only; see Section 2) are retained until you delete them or
                close your account, since they persist your personalization. Email{' '}
                <a href="mailto:privacy@salifly.com">privacy@salifly.com</a> to have them removed at any time.
              </li>
              <li>
                <strong>Screenshots.</strong> Not retained beyond the duration of the processing request that
                processes them (typically under 10 seconds).
              </li>
            </ul>
            <p>
              You can request immediate deletion of all data associated with your account at any time by
              contacting us at{' '}
              <a href="mailto:privacy@salifly.com">privacy@salifly.com</a>. We will process deletion
              requests within 30 days.
            </p>
          </section>

          <section>
            <h2>7. Your Rights</h2>
            <p>
              Depending on your jurisdiction, you may have the following rights regarding your personal data:
            </p>
            <ul>
              <li><strong>Access.</strong> Request a copy of the data we hold about you.</li>
              <li><strong>Correction.</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Deletion.</strong> Request deletion of your account and all associated personal data.</li>
              <li><strong>Portability.</strong> Request an export of your data in a machine-readable format (JSON).</li>
              <li><strong>Objection.</strong> Object to certain types of processing, such as analytics.</li>
              <li>
                <strong>Restriction.</strong> Request that we restrict processing of your data in certain
                circumstances.
              </li>
            </ul>
            <p>
              To exercise any of these rights, email{' '}
              <a href="mailto:privacy@salifly.com">privacy@salifly.com</a>. We will respond within 30 days.
              For users in the European Economic Area, you also have the right to lodge a complaint with
              your local supervisory authority.
            </p>
          </section>

          <section>
            <h2>8. Cookies and Browser Storage</h2>
            <p>
              The Salifly web website uses browser cookies and <code>localStorage</code> to maintain your
              authenticated session via Supabase. These session tokens expire automatically. We do not use
              tracking cookies, advertising cookies, or third-party analytics beyond our own product usage
              metrics.
            </p>
            <p>
              The Chrome extension uses <code>chrome.storage.local</code> (not browser cookies) to persist
              your session, conversation history, and user profile within the extension environment.
            </p>
          </section>

          <section>
            <h2>9. Security</h2>
            <p>
              We implement technical and organizational measures to protect your data, including:
            </p>
            <ul>
              <li>TLS encryption for all data in transit</li>
              <li>Encryption at rest in our Supabase PostgreSQL database</li>
              <li>Row Level Security (RLS) policies ensuring strict data isolation between users</li>
              <li>Analysis capabilities kept in the cloud, keeping the extension lightweight and your browser fast</li>
              <li>Regular security reviews of the extension and backend codebase</li>
            </ul>
            <p>
              No method of electronic transmission or storage is 100% secure. If you discover a security
              vulnerability, please report it responsibly to{' '}
              <a href="mailto:security@salifly.com">security@salifly.com</a>.
            </p>
          </section>

          <section>
            <h2>10. Children's Privacy</h2>
            <p>
              Salifly is designed for professional use and is not directed to individuals under 16 years of
              age. We do not knowingly collect personal information from children. If you believe a child
              has provided us with personal data, please contact us and we will promptly delete it.
            </p>
          </section>

          <section>
            <h2>11. International Data Transfers</h2>
            <p>
              Salifly is operated from the United States. If you are accessing the Service from outside the
              United States, your data may be transferred to, stored, and processed in the U.S. or other
              countries where our service providers operate. By using Salifly, you consent to this transfer.
              We ensure appropriate safeguards are in place consistent with applicable data protection laws.
            </p>
          </section>

          <section>
            <h2>12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes
              by email or by displaying a prominent notice within the Service at least 14 days before the
              changes take effect. Your continued use of Salifly after the effective date constitutes
              acceptance of the revised policy.
            </p>
          </section>

          <section>
            <h2>13. Contact</h2>
            <p>For privacy-related inquiries, contact us at:</p>
            <p>
              <strong>Salifly</strong><br />
              Email: <a href="mailto:privacy@salifly.com">privacy@salifly.com</a><br />
              For security issues: <a href="mailto:security@salifly.com">security@salifly.com</a>
            </p>
          </section>
        </div>
      </div>

      <footer className="page-footer">
        <div className="page-footer-inner">
          <div className="page-footer-row">
            <p className="page-footer-copy">© 2025 Salifly. All rights reserved.</p>
            <div className="page-footer-links">
              <button className="page-footer-link lp-footer-link-btn" onClick={() => onNavigate('privacy')}>Privacy</button>
              <button className="page-footer-link lp-footer-link-btn" onClick={() => onNavigate('terms')}>Terms</button>
              <a href="mailto:hello@salifly.com" className="page-footer-link">Contact</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
